import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import { MbChart } from 'components/MbChart';
import { useDispatch, useSelector } from 'react-redux';

import { useParams, useHistory } from 'react-router-dom';

import { cards } from 'constants/cards';

import {
  getDisplayValue,
  DEFAULT_MOBILE_DATE_FORMAT,
  filterRatesByCurrency,
  getUrlByCardType,
  parseDate,
  formatParsedDate,
} from 'utils';
import {
  navigateCurrencyDatePicker,
  prefetchCurrencyYearArchive,
} from 'utils/currencyYearArchive';

import React, { useEffect, useState } from 'react';
import dayjs from 'utils/dayjs';
import Icon from 'components/commons/Icon';
import DatePicker from 'components/commons/DatePicker';
import { CommentsLink } from 'components/ExchangeDiscussion/styles';
import { fetchCardsRates } from 'actions';
import { useSelectorProps } from 'hooks';

import Updated from 'components/commons/Updated';

import { getCardsRatesItems, getCurrentCurrency, getSeoMeta } from 'selectors';
import {
  CurrencyWrapper,
  TextDescription,
  CurrencyHeader,
  Row,
  TitleWrapper,
  TitleWithDateWrapper,
  Title,
  Titles,
  Source,
  TableAverage,
  TableColumn,
  TableFooterAverage,
  SubTitle,
} from './styles';

const titles = ['сurrency', 'purchase', 'sale'];

const terms = [
  { type: 'sell', value: 'cards' },
  { type: 'buy', value: 'cards' },
];

export const CardsAverageTable = ({ cardType, hasSubTitle }) => {
  const { date, currency = null } = useParams();
  const { locale } = useIntl();
  const dispatch = useDispatch();
  const selectorProps = useSelectorProps();
  const history = useHistory();
  const [dateValue, setDate] = useState(date);

  const getHref = currency => {
    const getCurrency = `${currency}/`;
    const getDate = date ? `${date}/` : '';

    return `/cards/${getCurrency}${getDate}`;
  };

  const onDateAccept = e => {
    const parsedDate = parseDate(e);

    if (!parsedDate) {
      return;
    }

    const formattedDate = parsedDate.format(DEFAULT_MOBILE_DATE_FORMAT);
    const getCurrency = currency ? `${currency}/` : '';
    const legacyPath = `/cards/${getCurrency}${formattedDate}/`;
    navigateCurrencyDatePicker({
      history,
      selectedDate: formattedDate,
      currency,
      legacyPath,
      locale,
      method: 'replace',
    });
  };

  const onDatePickerOpen = () => {
    prefetchCurrencyYearArchive({
      selectedDate: dateValue,
      currency,
      locale,
    });
  };

  const currentCurrency = useSelector(state =>
    getCurrentCurrency(state, { currency })
  );

  const cardsRatesItems = useSelector(state =>
    getCardsRatesItems(state, {
      dateKey: date || dayjs().format(DEFAULT_MOBILE_DATE_FORMAT),
      type: cardType,
    })
  );

  const { pageTitle, pageDescription } = useSelector(state =>
    getSeoMeta(state, selectorProps)
  );

  const cardsRates = filterRatesByCurrency(cardsRatesItems, currency);

  useEffect(() => {
    dispatch(
      fetchCardsRates({
        date: date || null,
        currency,
      })
    );
  }, [date, currency]);

  const titleMessage = currentCurrency ? (
    <FormattedMessage
      id={`cardsRates.title.withCurrency.${cardType}`}
      values={{
        currency: currentCurrency?.nameG,
        isDate: date ? 1 : 0,
        date: formatParsedDate(date, 'DD MMMM YYYY', locale),
      }}
    />
  ) : (
    <FormattedMessage
      id={`cardsRates.title.${cardType}`}
      values={{
        isDate: date ? 1 : 0,
        date: formatParsedDate(date, 'DD MMMM YYYY', locale),
      }}
    />
  );

  const subTitleMessage = currentCurrency ? (
    <FormattedMessage
      id="cardsRates.subTitle.withCurrency"
      values={{
        currency: currentCurrency?.nameG,
        isDate: date ? 1 : 0,
        date: date ? formatParsedDate(date, 'DD MMMM YYYY', locale) : '',
      }}
    />
  ) : (
    <FormattedMessage
      id="cardsRates.subTitle"
      values={{
        isDate: date ? 1 : 0,
        date: date ? formatParsedDate(date, 'DD MMMM YYYY', locale) : '',
      }}
    />
  );

  return (
    <CurrencyWrapper cardType={cardType}>
      <CurrencyHeader>
        <TitleWrapper cardType={cardType}>
          {!date && <Updated date={cardsRates[0]?.timestamp} />}

          <TitleWithDateWrapper>
            <Titles>
              {hasSubTitle && (
                <SubTitle>{pageDescription || subTitleMessage}</SubTitle>
              )}
              <Title>{pageTitle || titleMessage}</Title>
            </Titles>

            {cardType === cards.all && (
              <Row>
                <DatePicker
                  type="outline"
                  value={dateValue}
                  onChange={setDate}
                  getDisplayValue={getDisplayValue}
                  onOpen={onDatePickerOpen}
                  onAccept={e => onDateAccept(e)}
                />
              </Row>
            )}
          </TitleWithDateWrapper>

          {cardType === cards.all && (
            <TextDescription>
              <FormattedMessage id="cardsAverageRate" />
            </TextDescription>
          )}
        </TitleWrapper>
      </CurrencyHeader>
      <TableColumn>
        <TableAverage
          titles={titles}
          rows={cardsRates}
          type="cash"
          getHref={getHref}
          activeCurrency={currency}
        />
      </TableColumn>
      <TableFooterAverage>
        {cardType !== cards.all && (
          <CommentsLink
            href={getUrlByCardType(cardType, locale)}
            target="_blank"
          >
            <FormattedMessage id={`table.cards.${cardType}`} />
            <Icon name="vectorStrokeMb" style={{ marginLeft: '10px' }} />
          </CommentsLink>
        )}
        <Source mode="mobile">
          <FormattedMessage id="table.source.mb.desc" />
        </Source>

        <Source mode="desktop">
          <FormattedMessage id="table.source.desc" />
        </Source>
      </TableFooterAverage>

      <MbChart
        terms={terms}
        type="cards"
        chartType="cards"
        chartTitle="dynamic.rates.chart.cards.title"
        isOpen={cardType === cards.all}
      />
    </CurrencyWrapper>
  );
};

CardsAverageTable.propTypes = {
  cardType: PropTypes.string.isRequired,
  hasSubTitle: PropTypes.bool,
};

CardsAverageTable.defaultProps = {
  hasSubTitle: false,
};
